import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { FaWhatsapp, FaMapMarkerAlt, FaCalendarAlt, FaUsers, FaStar, FaCheck, FaTimes, FaPlaneDeparture, FaPlaneArrival, FaHotel, FaCar } from 'react-icons/fa';
import BookingForm from '../../components/common/BookingForm';
import { companyInfo } from '../../data/tourData';
import './Kerala.css';

const Kerala = () => {
  return (
    <>
      <Helmet>
        <title>Kerala Tour - NomadTrailz</title>
        <meta name="description" content="Experience God's Own Country - Kerala with NomadTrailz. Backwaters, beaches, and rich cultural heritage." />
      </Helmet>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        {/* Hero Section */}
        <section className="destination-hero">
          <div className="hero-background">
            <img 
              src="https://res.cloudinary.com/duym2bgp2/image/upload/v1755000421/pzput1rlwtpqynyiqaw4.jpg" 
              alt="Kerala" 
            />
            <div className="hero-overlay"></div>
          </div>
          <div className="hero-content">
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              Kerala
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              God's Own Country with backwaters, beaches, and rich cultural heritage
            </motion.p>
          </div>
        </section>

        {/* Tour Details */}
        <section className="tour-details">
          <div className="container">
            <div className="details-grid">
              <div className="tour-info">
                <motion.div 
                  className="info-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h2>Tour Overview</h2>
                  <p>
                    Kerala, known as "God's Own Country," is a tropical paradise with pristine 
                    beaches, serene backwaters, lush hill stations, and rich cultural heritage. 
                    Experience the perfect blend of nature, culture, and relaxation in this 
                    beautiful southern state of India.
                  </p>
                </motion.div>

                <motion.div 
                  className="highlights-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <h3>Highlights</h3>
                  <ul>
                    <li>Houseboat cruise in backwaters</li>
                    <li>Ayurvedic treatments and wellness</li>
                    <li>Beach relaxation and water sports</li>
                    <li>Hill station exploration</li>
                    <li>Traditional Kathakali performances</li>
                    <li>Spice plantation tours</li>
                  </ul>
                </motion.div>

                <motion.div 
                  className="itinerary-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <h3>Itinerary</h3>
                  <div className="day-item">
                    <h4>Day 1: Arrival at Kochi Airport and Transfer to Munnar</h4>
                    <ul>
                      <li>Arrival at Kochi Airport (COK) / Railway station (ERS)</li>
                      <li>Transfer to Munnar, the tea county of central Kerala</li>
                      <li>Visit Cheeyappara waterfalls and Valara Waterfalls en route</li>
                      <li>Explore tea plantations planted by Britishers 200 years back</li>
                      <li>Check in at the resort and overnight stay</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 2: Full day sightseeing tour of Munnar</h4>
                    <ul>
                      <li>Visit Eravikulam National Park with highest peak Anamudi</li>
                      <li>See the extinct species of Mountain Goat - Nilgiri Tahr</li>
                      <li>Explore Tea Museum showcasing Munnar's history</li>
                      <li>Visit Blossom Park, Echo Point, Mattupetty Dam</li>
                      <li>Explore Kundala Lake and Top Station</li>
                      <li>Return to hotel by evening</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 3: Drive to the cardamom hills of Thekkady</h4>
                    <ul>
                      <li>Drive through tea, cardamom and pepper plantations</li>
                      <li>Visit Spice Gardens en route</li>
                      <li>Explore Elephant park and Periyar National Park</li>
                      <li>Enjoy common boat ride to see wildlife</li>
                      <li>Spot Elephants, Wild Boar, Sambar Deer and Indian Guar</li>
                      <li>Overnight stay at Thekkady resort</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 4: Drive to Alleppey backwaters</h4>
                    <ul>
                      <li>Check out from Thekkady and drive to Alleppey</li>
                      <li>Board exclusive Keralan Houseboat</li>
                      <li>Cruise along narrow canals experiencing local backwater life</li>
                      <li>All meals served on board from Lunch to next day's breakfast</li>
                      <li>Exclusive houseboat with air conditioning during sleeping hours</li>
                      <li>Overnight stay on houseboat</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 5: Drive to Kovalam</h4>
                    <ul>
                      <li>Check out from houseboat and drive to Kovalam</li>
                      <li>Explore renowned beach destination near Thiruvananthapuram</li>
                      <li>Visit three crescent-shaped beaches - Lighthouse, Hawah, Samudra</li>
                      <li>Check in at resort and relax</li>
                      <li>Enjoy resort activities and overnight stay</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 6: Day excursion to Kanyakumari</h4>
                    <ul>
                      <li>Full day excursion to southernmost tip of Indian peninsula</li>
                      <li>Visit Padmanabhapuram Palace - largest wooden palace in Asia</li>
                      <li>Explore Suchindram Temple</li>
                      <li>Visit Vivekananda rock memorial and Thiruvalluvar statue</li>
                      <li>Witness convergence of Arabian Sea, Indian Ocean, and Bay of Bengal</li>
                      <li>Return to Kovalam for overnight stay</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 7: Sightseeing tour and departure from Thiruvananthapuram</h4>
                    <ul>
                      <li>Visit Sree Padmanabhaswamy Temple dedicated to Lord Vishnu</li>
                      <li>Explore Kuthiramalika Palace with unique architecture</li>
                      <li>Visit Napier Museum showcasing historic artifacts</li>
                      <li>Explore capital city's rich cultural heritage</li>
                      <li>Evening departure transfer to Thiruvananthapuram Airport (TRV)</li>
                    </ul>
                  </div>
                </motion.div>

                <motion.div 
                  className="inclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <h3>Inclusions</h3>
                  <ul>
                    <li><FaCheck className="check-icon" />All transfers and sightseeing in private vehicle</li>
                    <li><FaCheck className="check-icon" />Hotel accommodation with breakfast</li>
                    <li><FaCheck className="check-icon" />Boat safari tickets at Periyar National Park</li>
                    <li><FaCheck className="check-icon" />Spice Plantation tour in Thekkady</li>
                    <li><FaCheck className="check-icon" />Houseboat day cruise with all meals</li>
                    <li><FaCheck className="check-icon" />Professional tour guide</li>
                    <li><FaCheck className="check-icon" />24/7 customer support</li>
                  </ul>
                </motion.div>

                <motion.div 
                  className="exclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <h3>Exclusions</h3>
                  <ul>
                    <li><FaTimes className="times-icon" />Flight/train tickets to and from Kerala</li>
                    <li><FaTimes className="times-icon" />Lunch and dinner (except houseboat meals)</li>
                    <li><FaTimes className="times-icon" />Entrance fees to monuments and museums</li>
                    <li><FaTimes className="times-icon" />Camera charges at tourist spots</li>
                    <li><FaTimes className="times-icon" />Personal expenses and tips</li>
                    <li><FaTimes className="times-icon" />Travel insurance</li>
                  </ul>
                </motion.div>
              </div>

              <div className="booking-section">
                <motion.div 
                  className="booking-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h3>Book This Tour</h3>
                  <div className="price-info">
                    <span className="price">₹ 18,999</span>
                    <span className="duration">7 Days / 6 Nights</span>
                  </div>
                  
                  <div className="tour-features">
                    <div className="feature">
                      <FaMapMarkerAlt />
                      <span>Kerala, South India</span>
                    </div>
                    <div className="feature">
                      <FaCalendarAlt />
                      <span>Best Time: October to March</span>
                    </div>
                    <div className="feature">
                      <FaUsers />
                      <span>Group Size: 8-12 people</span>
                    </div>
                    <div className="feature">
                      <FaStar />
                      <span>Difficulty: Easy</span>
                    </div>
                  </div>

                </motion.div>

                <motion.div 
                  className="tour-logistics-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                >
                  <h3>Tour Logistics</h3>
                  <div className="logistics-grid">
                    <div className="logistics-item">
                      <FaPlaneDeparture className="logistics-icon" />
                      <div className="logistics-content">
                        <span className="logistics-label">Pickup Point:</span>
                        <span className="logistics-value">Kochi Airport (COK)</span>
                      </div>
                    </div>
                    <div className="logistics-item">
                      <FaPlaneArrival className="logistics-icon" />
                      <div className="logistics-content">
                        <span className="logistics-label">Drop Point:</span>
                        <span className="logistics-value">Thiruvananthapuram Airport (TRV)</span>
                      </div>
                    </div>
                    <div className="logistics-item">
                      <FaHotel className="logistics-icon" />
                      <div className="logistics-content">
                        <span className="logistics-label">Accommodation:</span>
                        <span className="logistics-value">Munnar (2N) - Thekkady (1N) - Alleppey (1N) - Kovalam (2N)</span>
                      </div>
                    </div>
                    <div className="logistics-item">
                      <FaCar className="logistics-icon" />
                      <div className="logistics-content">
                        <span className="logistics-label">Vehicle:</span>
                        <span className="logistics-value">Suzuki Ertiga or Kia Carens</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <BookingForm 
                  tourName="Kerala Backwaters & Beaches Tour"
                  tourPrice="₹ 18,999"
                  tourDuration="7 Days / 6 Nights"
                />
              </motion.div>
            </div>
          </div>
        </section>
      </motion.div>
    </>
  );
};

export default Kerala;
